import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CheckoutLanguage } from '@platform-frontend/checkout-drop-in';
import { combineLatest, map, Observable, Subject, tap } from 'rxjs';
import { DateSelectionValue } from '../model/date-selection-value';

@UntilDestroy()
@Component({
  selector: 'yat-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true
    }
  ]
})
export class DateInputComponent implements ControlValueAccessor, OnInit {
  constructor(private readonly translocoService: TranslocoService, private readonly cdref: ChangeDetectorRef) {}

  readonly valueChanged$: Subject<DateSelectionValue> = new Subject<DateSelectionValue>();
  language$: Observable<CheckoutLanguage>;
  value: string = '';
  showMask: boolean = false;

  @Input()
  readonly isInvalid?: boolean = false;

  @Output()
  readonly dateInputBlur = new EventEmitter<boolean>();

  @Output()
  readonly dateInputFocus = new EventEmitter<boolean>();

  onChange: (value: DateSelectionValue) => void;

  onTouched: () => void;

  ngOnInit(): void {
    this.language$ = this.translocoService.langChanges$.pipe(map(lang => lang as CheckoutLanguage));
    combineLatest([this.language$, this.valueChanged$])
      .pipe(
        tap(([lang, val]) => this.setValueFromDate(lang, val)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  writeValue(newDate: DateSelectionValue): void {
    this.valueChanged$.next(newDate);
  }

  registerOnChange(fn: (value: DateSelectionValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onInputChange(value: string): void {
    this.value = value;

    if (!value) {
      this.onChange(null);
      return;
    }

    const formattedDate = this.formatStringToDate(this.translocoService.getActiveLang() as CheckoutLanguage, value);

    this.onChange(formattedDate);
  }

  handleBlur(value: string): void {
    this.showMask = false;
    this.onTouched();
    this.dateInputBlur.emit(!!value && value.length > 0);
  }

  handleFocus(): void {
    this.showMask = true;
    this.cdref.detectChanges();
    this.dateInputFocus.emit();
  }

  private setValueFromDate(lang: CheckoutLanguage, date: DateSelectionValue): void {
    if (!date || date === 'INVALID') {
      this.value = '';
      return;
    }

    const delimiter = lang === 'de' ? '.' : '/';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    const newDate = `${day}${delimiter}${month}${delimiter}${year}`;
    this.value = lang === 'de' ? this.value.replace(/\//g, '.') : this.value.replace(/\./g, '/');

    if (this.value !== newDate) {
      this.value = newDate;
      this.cdref.markForCheck();
    }
  }

  private formatStringToDate(lang: CheckoutLanguage, value: string): DateSelectionValue {
    const delimiter = lang === 'de' ? '.' : '/';
    const cleanedValue = value.replace(new RegExp(`[^0-9${delimiter}]`, 'g'), '');

    if (cleanedValue.length < 10) {
      return 'INVALID';
    }

    const splittedValue = value.split(delimiter);
    const basicDatePattern: RegExp =
      /^(?:(?:(?:0?[13578]|1[02])([/.-])31)\1|(?:(?:0?[1,3-9]|1[0-2])([/.-])(?:29|30)\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:0?2([/.-])29\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:(?:0?[1-9])|(?:1[0-2]))([/.-])(?:0?[1-9]|1\d|2[0-8])\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/;
    if (!basicDatePattern.test(splittedValue[1] + '/' + splittedValue[0] + '/' + splittedValue[2])) {
      return 'INVALID';
    }

    const reversedDateArray = [...splittedValue].reverse();
    const newDate = new Date(reversedDateArray.join('-'));
    return newDate;
  }
}
